import { useEffect, useRef, useState } from "react";
import { api, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";

/**
 * Public Guestbook / Photo-wall section.
 * Shown on every published invitation when `data.guestbook_enabled !== false`.
 * Guests post a name + message + optional photo; entries go into moderation
 * and only appear after the owner approves them.
 *
 * Uses the public upload endpoint /api/public/uploads/guestbook so unauthenticated
 * visitors can attach photos.
 */
export default function GuestbookSection({ slug, accent = "#B89B5E", isPreview = false }) {
  const [entries, setEntries] = useState([]);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captcha, setCaptcha] = useState({ a: 0, b: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const fileRef = useRef(null);

  // Generate a random simple math captcha
  const regenCaptcha = () => {
    const a = Math.floor(Math.random() * 8) + 2; // 2–9
    const b = Math.floor(Math.random() * 8) + 2; // 2–9
    setCaptcha({ a, b });
    setCaptchaAnswer("");
  };

  useEffect(() => { regenCaptcha(); }, []);

  const load = async () => {
    if (isPreview || !slug) { setEntries([]); return; }
    try {
      const r = await api.get(`/public/invitations/${slug}/guestbook`);
      setEntries(r.data || []);
    } catch {
      setEntries([]);
    }
  };

  useEffect(() => {
    load();
    // Auto-scroll to section if URL points here
    if (typeof window !== "undefined" && window.location.hash === "#guestbook") {
      setTimeout(() => {
        document.getElementById("guestbook")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [slug, isPreview]);

  const pickPhoto = () => fileRef.current?.click();

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image"); return; }
    if (file.size > 4 * 1024 * 1024) { toast.error("Photo too large (max 4MB)"); return; }
    if (isPreview) {
      // Local preview only — don't hit the public upload endpoint without a slug
      const localUrl = URL.createObjectURL(file);
      setPhotoUrl(localUrl);
      toast.success("Preview mode — photo attached locally");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/public/uploads/guestbook?slug=${encodeURIComponent(slug)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = `${BACKEND_URL}${r.data.url}`;
      setPhotoUrl(fullUrl);
      toast.success("Photo attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !message.trim()) {
      toast.error("Please add your name and a short message");
      return;
    }
    const ans = parseInt(captchaAnswer, 10);
    if (Number.isNaN(ans)) {
      toast.error("Please answer the verification sum");
      return;
    }
    if (isPreview) {
      toast.success("Preview mode — guest messages will be saved on the published site.");
      setName(""); setMessage(""); setPhotoUrl("");
      setSubmitted(true);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/public/invitations/${slug}/guestbook`, {
        name: name.trim(),
        message: message.trim(),
        photo_url: photoUrl || null,
        captcha_a: captcha.a,
        captcha_b: captcha.b,
        captcha_answer: ans,
      });
      toast.success("Thank you! Your note will appear after the couple approves it.");
      setName(""); setMessage(""); setPhotoUrl("");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
      regenCaptcha();
    } finally {
      setBusy(false);
    }
  };

  const fld = "w-full bg-white/80 border border-stone-300 px-4 py-3 text-sm text-stone-900 focus:outline-none focus:border-stone-900 placeholder:text-stone-400";

  return (
    <section id="guestbook" className="py-20 md:py-28 px-6 border-t bg-[#FAF6EE] relative overflow-hidden scroll-mt-4" style={{ borderColor: `${accent}40`, color: "#1A1918" }} data-testid="guestbook-section">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <div className="text-[10px] uppercase tracking-[0.5em] mb-3" style={{ color: accent }}>Guestbook · Photo-wall</div>
          <h2 className="font-serif-display text-4xl md:text-6xl font-light italic">Leave a note for us</h2>
          <p className="opacity-70 mt-3 text-sm md:text-base max-w-xl mx-auto">Share a wish, a memory, or a photo — we'd love to treasure it forever.</p>
          {isPreview && (
            <div className="inline-block mt-5 px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] border" style={{ background: `${accent}14`, color: accent, borderColor: `${accent}60` }} data-testid="guestbook-preview-banner">
              Preview · this is exactly how guests will see the wall
            </div>
          )}
        </div>

        {/* FORM */}
        {!submitted ? (
          <form onSubmit={submit} className="max-w-xl mx-auto bg-white border p-6 md:p-8 mb-16" style={{ borderColor: `${accent}60` }} data-testid="guestbook-form">
            <div className="space-y-3">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className={fld} maxLength={80} data-testid="guestbook-name-input" />
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Your message for the couple…" rows={4} maxLength={600} className={fld} data-testid="guestbook-message-input" />

              <input ref={fileRef} type="file" accept="image/*" onChange={uploadPhoto} className="hidden" data-testid="guestbook-photo-input" />
              {photoUrl ? (
                <div className="relative aspect-video bg-stone-100 border border-stone-300 overflow-hidden">
                  <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setPhotoUrl("")} className="absolute top-2 right-2 px-3 py-1 bg-white/90 text-xs uppercase tracking-[0.2em]" data-testid="guestbook-photo-remove">Remove</button>
                </div>
              ) : (
                <button type="button" onClick={pickPhoto} disabled={uploading} className="w-full py-3 border border-dashed border-stone-400 text-xs uppercase tracking-[0.3em] text-stone-600 hover:bg-stone-50 disabled:opacity-60" data-testid="guestbook-photo-pick">
                  {uploading ? "Uploading…" : "+ Add a photo (optional)"}
                </button>
              )}

              {/* Anti-bot verification */}
              <div className="bg-stone-50 border border-stone-300 p-3" data-testid="guestbook-captcha">
                <div className="text-[10px] uppercase tracking-[0.3em] text-stone-500 mb-2">Verification · prevents spam</div>
                <div className="flex items-center gap-3 flex-nowrap">
                  <span className="font-serif-display text-lg whitespace-nowrap" style={{ color: accent }}>{captcha.a} + {captcha.b} =</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={captchaAnswer}
                    onChange={(e) => setCaptchaAnswer(e.target.value)}
                    placeholder="?"
                    className="flex-1 min-w-0 px-3 py-2 border border-stone-300 text-center focus:outline-none focus:border-stone-900"
                    data-testid="guestbook-captcha-input"
                    required
                  />
                </div>
              </div>

              <button type="submit" disabled={busy || uploading} className="w-full py-3 text-xs uppercase tracking-[0.3em] text-white transition-opacity disabled:opacity-60" style={{ background: accent }} data-testid="guestbook-submit-btn">
                {busy ? "Sending…" : "Leave your message"}
              </button>
              <div className="text-[10px] text-stone-500 text-center">Messages are reviewed before appearing on the wall.</div>
            </div>
          </form>
        ) : (
          <div className="max-w-xl mx-auto bg-white border p-8 mb-16 text-center" style={{ borderColor: `${accent}60` }} data-testid="guestbook-thanks">
            <div className="font-serif-display text-3xl italic" style={{ color: accent }}>Thank you ♥</div>
            <div className="mt-3 text-stone-600">Your note will appear on the wall shortly.</div>
            <button onClick={() => setSubmitted(false)} className="mt-6 text-xs uppercase tracking-[0.3em] underline underline-offset-4">Leave another</button>
          </div>
        )}

        {/* WALL */}
        {entries.length > 0 && (
          <div className="mt-4">
            <div className="text-center mb-8">
              <div className="text-[10px] uppercase tracking-[0.5em]" style={{ color: accent }}>{entries.length} notes so far</div>
            </div>
            <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6" data-testid="guestbook-wall">
              {entries.map((e) => (
                <article key={e.id} className="break-inside-avoid bg-white border p-5" style={{ borderColor: `${accent}40` }} data-testid={`guestbook-entry-${e.id}`}>
                  {e.photo_url && (
                    <img src={e.photo_url.startsWith("/") ? `${BACKEND_URL}${e.photo_url}` : e.photo_url} alt="" className="w-full h-auto mb-4 object-cover" loading="lazy" />
                  )}
                  <div className="font-serif-display text-lg italic" style={{ color: accent }}>— {e.name}</div>
                  <p className="mt-2 text-sm text-stone-700 leading-relaxed whitespace-pre-line">{e.message}</p>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-stone-400">
                    {new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
